import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import Box from "../../../../components/Box";
import { Helpers } from "../../../../helpers/Helpers";
import { KTCard } from "../../../../_metronic/helpers/components/KTCard";
import { KTCardBody } from "../../../../_metronic/helpers/components/KTCardBody";
import { storeActivityIndicator, storeAreYouSureModalCallback } from "../../../../redux/reducers/authSlice";
import CustomNumberInput from "../../../../components/CustomNumberInput";
import CustomTextInput from "../../../../components/CustomTextInput";
import CustomDatePicker from "../../../../components/CustomDatePicker";
import { PersonelUcret, PersonelUcretService } from "../../../../services/PersonelUcretService";
import { CalismaSaatiService } from "../../../../services/CalismaSaatiService";

interface Props {
    personnelId: string
}

interface UcretSatiri extends PersonelUcret {
    isSelected: boolean
}

/** Sayı → form alanı metni (virgüllü). */
const metin = (n: number | null | undefined) => n == null ? '' : String(n).replace('.', ',');

const yuvarla = (n: number) => Math.round(n * 100) / 100;

/**
 * Personel kartındaki "Ücret" bölümü (11.10.2026; Sabit Gelir dağıtım grid kalıbı).
 * Ücret tarihli tutulur; her kaydet / sil hemen backend'e gider (personel formunun Kaydet'ini beklemez) ve grid yenilenir.
 * Yalnız "Personel Ücretleri" yetkisi olana gösterilir (backend de yetkisizi reddeder).
 */
const PersonelUcretleri = ({ personnelId }: Props) => {
    const dispatch = useDispatch();
    const ucretRef = useRef<any>(null);

    const [ucretler, setUcretler] = useState<UcretSatiri[]>([]);
    const [saatlikBolen, setSaatlikBolen] = useState<number>(200);
    const [carpanHaftaIci, setCarpanHaftaIci] = useState<number>(1.5);

    // Grid'deki ekleme / güncelleme formu
    const [isOpen, setIsOpen] = useState(false);
    const [duzenlenenId, setDuzenlenenId] = useState<string | undefined>(undefined);
    const [gecerlilikBaslangic, setGecerlilikBaslangic] = useState<string | undefined>(undefined);
    const [aylikMaas, setAylikMaas] = useState<string | undefined>(undefined);
    const [saatlikUcret, setSaatlikUcret] = useState<string | undefined>(undefined);
    const [aciklama, setAciklama] = useState<string | undefined>(undefined);

    useEffect(() => {
        getCalismaSaati();
        getData();
    }, [personnelId])

    const getCalismaSaati = async () => {
        try {
            var response = await CalismaSaatiService.getir();

            if (response.isSuccess && response.data) {
                setSaatlikBolen(response.data.saatlikBolen || 200);
                setCarpanHaftaIci(response.data.carpanHaftaIci ?? 1.5);
            }
        } catch {
            // Varsayılanlar (200 / 1,5) kalır.
        }
    }

    const getData = async () => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await PersonelUcretService.liste(personnelId);

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            setUcretler((response.data ?? []).map(x => ({ ...x, isSelected: false })));
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    // ---------------------------------------------------------------- Form

    const scrollTo = () => {
        if (!ucretRef.current)
            return;

        var headerOffset = 120;
        var elementPosition = ucretRef.current.getBoundingClientRect().top;
        var offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
        });
    }

    const formuTemizle = () => {
        setDuzenlenenId(undefined);
        setGecerlilikBaslangic(undefined);
        setAylikMaas(undefined);
        setSaatlikUcret(undefined);
        setAciklama(undefined);
    }

    const cancelAdding = () => {
        formuTemizle();
        setIsOpen(!isOpen);
        scrollTo();
    }

    /** Aylık maaş değişince saatlik ücret önerilir (maaş ÷ bölen); kullanıcı sonra değiştirebilir. */
    const aylikMaasDegisti = (deger: string | undefined) => {
        setAylikMaas(deger);

        var maas = Helpers.toNumber(deger);
        setSaatlikUcret(maas > 0 && saatlikBolen > 0 ? metin(yuvarla(maas / saatlikBolen)) : undefined);
    }

    const saveItem = async () => {
        try {
            if (!gecerlilikBaslangic) {
                toast.error("Geçerlilik başlangıcı alanı zorunludur.");
                return;
            }

            if (Helpers.isEmpty(aylikMaas)) {
                toast.error("Aylık maaş alanı zorunludur.");
                return;
            }

            var maas = Helpers.toNumber(aylikMaas);
            var saatlik = Helpers.toNumber(saatlikUcret);

            if (maas < 0) {
                toast.error("Aylık maaş 0 ya da daha büyük olmalıdır.");
                return;
            }

            if (!(saatlik > 0)) {
                toast.error("Saatlik ücret 0'dan büyük olmalıdır.");
                return;
            }

            if ((aciklama ?? '').trim().length > 500) {
                toast.error("Açıklama en çok 500 karakter olabilir.");
                return;
            }

            if (ucretler.some(x => x.gecerlilikBaslangic == gecerlilikBaslangic && x.personelUcretId != duzenlenenId)) {
                toast.error("Bu tarihte bir ücret kaydı zaten var.");
                return;
            }

            dispatch(storeActivityIndicator(true));

            var response = await PersonelUcretService.kaydet({
                personelUcretId: duzenlenenId,
                personnelId: personnelId,
                gecerlilikBaslangic: gecerlilikBaslangic,
                aylikMaas: maas,
                saatlikUcret: saatlik,
                aciklama: aciklama || undefined
            });

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                dispatch(storeActivityIndicator(false));
                return;
            }

            toast.success("Ücret kaydedildi.");

            formuTemizle();
            setIsOpen(false);
            scrollTo();
            await getData();
        } catch (error: any) {
            toast.error(error);
            dispatch(storeActivityIndicator(false));
        }
    }

    // ---------------------------------------------------------------- Grid

    const setItemSelected = (_id: string) => {
        setUcretler(ucretler.map(x => x.personelUcretId == _id ? { ...x, isSelected: !x.isSelected } : x));
    }

    const selectAll = () => {
        var isAllSelected = ucretler.length == ucretler.filter(x => x.isSelected).length;

        setUcretler(ucretler.map(x => ({ ...x, isSelected: !isAllSelected })));
    }

    const setUpdateItem = () => {
        var o = ucretler.filter(x => x.isSelected)[0];

        setDuzenlenenId(o.personelUcretId);
        setGecerlilikBaslangic(o.gecerlilikBaslangic);
        setAylikMaas(metin(o.aylikMaas));
        setSaatlikUcret(metin(o.saatlikUcret));
        setAciklama(o.aciklama ?? undefined);

        setIsOpen(true);
        scrollTo();
    }

    const deleteItems = async () => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await PersonelUcretService.sil(ucretler.filter(x => x.isSelected).map(x => x.personelUcretId));

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                dispatch(storeActivityIndicator(false));
                return;
            }

            toast.success("Ücret kaydı silindi.");

            formuTemizle();
            setIsOpen(false);
            await getData();
        } catch (error: any) {
            toast.error(error);
            dispatch(storeActivityIndicator(false));
        }
    }

    const seciliSayisi = ucretler.filter(x => x.isSelected).length;

    return (
        <div className='containerPart' ref={ucretRef}>
            <KTCard className="ebbe-card mt-10">
                <KTCardBody>
                    <h4 className="mb-10 text-black">Ücret</h4>

                    {
                        isOpen && (
                            <React.Fragment>
                                <div className="row">
                                    <div className="col-md-3">
                                        <CustomDatePicker labelText="Geçerlilik başlangıcı" isLabelRequired value={gecerlilikBaslangic} setValue={setGecerlilikBaslangic} />
                                    </div>

                                    <div className="col-md-3">
                                        <CustomNumberInput labelText="Aylık maaş" isLabelRequired value={aylikMaas} setValue={aylikMaasDegisti} />
                                    </div>

                                    <div className="col-md-3">
                                        <CustomNumberInput labelText={`Saatlik ücret (maaş ÷ ${saatlikBolen})`} isLabelRequired value={saatlikUcret} setValue={setSaatlikUcret} />
                                    </div>

                                    <div className="col-md-3">
                                        <CustomTextInput labelText="Açıklama" value={aciklama} setValue={setAciklama} />
                                    </div>
                                </div>

                                <div className="row mt-5">
                                    <div className="col-md-12">
                                        <Box alignItems="flex-end">
                                            <Box onClick={saveItem} className="actionIcon p-2 bg-success" justifyContent="center" flexDirection="row" borderRadius="4px">
                                                <Box mr="5px" justifyContent="center">
                                                    <i className="text-white fa fa-check" />
                                                </Box>
                                                <Box justifyContent="center">
                                                    <span style={{ fontWeight: 600 }} className="text-white">Kaydet</span>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </div>
                                </div>

                                <Box className='ebbe-hr m-0 mt-5'>&nbsp;</Box>
                            </React.Fragment>
                        )
                    }

                    <Box className={isOpen ? "mt-5" : ""} flexDirection="row">
                        <Box onClick={cancelAdding} className={"actionIcon p-2 " + (!isOpen ? "bg-success" : "")} opacity={"1"} bg={isOpen ? 'orange' : undefined} justifyContent="center" flexDirection="row" borderRadius="4px">
                            <Box mr="5px" justifyContent="center">
                                <i className={"text-white fa " + (!isOpen ? 'fa-plus' : 'fa-times')} />
                            </Box>
                            <Box justifyContent="center">
                                <span style={{ fontWeight: 600 }} className="text-white">{!isOpen ? 'Ekle' : 'İptal Et'}</span>
                            </Box>
                        </Box>

                        <Box onClick={seciliSayisi == 1 ? setUpdateItem : undefined} opacity={seciliSayisi == 1 ? "1" : "0.5"} ml="10px" className="actionIcon p-2 bg-primary" justifyContent="center" flexDirection="row" borderRadius="4px">
                            <Box mr="5px" justifyContent="center">
                                <i className="text-white fa fa-edit" />
                            </Box>
                            <Box justifyContent="center">
                                <span style={{ fontWeight: 600 }} className="text-white">Güncelle</span>
                            </Box>
                        </Box>

                        <Box onClick={seciliSayisi > 0 ? () => dispatch(storeAreYouSureModalCallback(deleteItems)) : undefined} opacity={seciliSayisi > 0 ? "1" : "0.5"} ml="10px" className="actionIcon p-2 bg-danger" justifyContent="center" flexDirection="row" borderRadius="4px">
                            <Box mr="5px" justifyContent="center">
                                <i className="text-white fa fa-trash" />
                            </Box>
                            <Box justifyContent="center">
                                <span style={{ fontWeight: 600 }} className="text-white">Sil</span>
                            </Box>
                        </Box>
                    </Box>

                    <div className="row mt-5">
                        <div className="col-md-12">
                            <div className='table-responsive'>
                                <table id='kt_table_users' className='table align-middle table-row-dashed dataTable no-footer'>
                                    <thead>
                                        <tr className='text-start text-muted fw-bolder text-uppercase'>
                                            <th style={{ width: 1 }}>
                                                <div className='d-flex'>
                                                    <label className='form-check form-check-sm form-check-custom form-check-solid'>
                                                        <input onChange={selectAll} className='form-check-input' type='checkbox' checked={seciliSayisi > 0 && seciliSayisi == ucretler.length} />
                                                    </label>
                                                </div>
                                            </th>
                                            <th>Geçerlilik</th>
                                            <th className='custom-text-right'>Aylık maaş</th>
                                            <th className='custom-text-right'>Saatlik ücret</th>
                                            <th className='custom-text-right'>Hafta içi ek mesai saat ücreti</th>
                                            <th>Açıklama</th>
                                            <th>Oluşturan</th>
                                        </tr>
                                    </thead>
                                    <tbody className='text-gray-600 fw-bold'>
                                        {
                                            ucretler.length == 0 && (
                                                <tr>
                                                    <td className="text-center" colSpan={99}>Kayıt bulunamadı.</td>
                                                </tr>
                                            )
                                        }
                                        {
                                            ucretler.map(item =>
                                                <tr key={item.personelUcretId}>
                                                    <td style={{ width: 1 }}>
                                                        <div className='d-flex'>
                                                            <label className='form-check form-check-sm form-check-custom form-check-solid'>
                                                                <input onChange={() => setItemSelected(item.personelUcretId)} className='form-check-input' type='checkbox' checked={item.isSelected} />
                                                            </label>
                                                        </div>
                                                    </td>
                                                    <td onClick={() => setItemSelected(item.personelUcretId)}>
                                                        {item.gecerlilikBaslangicStr}
                                                        {item.gecerli && <span className="badge badge-light-success ebbe-status-badge ms-3">Geçerli</span>}
                                                    </td>
                                                    <td className='custom-text-right' onClick={() => setItemSelected(item.personelUcretId)}>{Helpers.correctPrice(item.aylikMaas, 2)}</td>
                                                    <td className='custom-text-right' onClick={() => setItemSelected(item.personelUcretId)}>{Helpers.correctPrice(item.saatlikUcret, 2)}</td>
                                                    <td className='custom-text-right' onClick={() => setItemSelected(item.personelUcretId)}>{Helpers.correctPrice(yuvarla(item.saatlikUcret * carpanHaftaIci), 2)}</td>
                                                    <td onClick={() => setItemSelected(item.personelUcretId)}>{item.aciklama}</td>
                                                    <td onClick={() => setItemSelected(item.personelUcretId)}>{item.createdUser} - {item.createdDate}</td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                </table>
                            </div>

                            <div className="text-muted fs-7">
                                Ücret geçmişi tarihli tutulur; zam sonrası eski ayların puantajı eski ücretle hesaplanır. Bu bilgileri yalnız 'Personel Ücretleri' yetkisi olan roller görür.
                                {' '}Hafta içi ek mesai saat ücreti = saatlik ücret × {String(carpanHaftaIci).replace('.', ',')} (İnsan Kaynakları › Tanımlamalar › Çalışma Saatleri).
                            </div>
                        </div>
                    </div>
                </KTCardBody>
            </KTCard>
        </div>
    )
}

export default PersonelUcretleri;
